<script setup>
import { WdsFooter } from '@wds/components';
import { useCurrencyStore } from '@/stores/currency';
import { useLanguageStore } from '@/stores/language';
import { getLegalLinks } from './sections/legal-links';
import { getCommonLinks } from './sections/quick-links';
import { getSocialMediaLinks } from './sections/social-media-links';

const props = defineProps({
  isShortVersion: {
    type: Boolean,
    required: false,
    default: false,
  },
});

// ### COMPOSABLES
const { t } = useI18n();
const { useUrl } = useUrls();
const { isExtraSmallScreen } = useMq();

// ### STORES
const languageStore = useLanguageStore();
const currencyStore = useCurrencyStore();
const testingStore = useTestingStore();

// ### EXPERIMENTS
const isMeetTheWorldEnabled = testingStore.hasEnabledFeature('web_meetTheWorldOnFooter');

// ### VARIABLES INITIALIZATION
const quickLinks = getCommonLinks({ useUrl });
const socialMediaLinks = getSocialMediaLinks();

// ### DATA
const wdsFooterRef = ref(null);
const { isShortVersion } = toRefs(props);

// ### COMPUTED
const getQuickLinks = computed(() => {
  if (isExtraSmallScreen.value) {
    return quickLinks.mobile;
  }
  return quickLinks.desktop;
});
const getFooterData = computed(() => {
  return {
    isShortVersion: isShortVersion?.value,
    copyrightYear: new Date().getFullYear(),
    currentLanguage: languageStore?.getCurrentLanguage?.name,
    currentCurrency: currencyStore?.getCurrentCurrency?.code,
    socialMediaLinks: socialMediaLinks?.getSocialNetworks,
    quickLinks: getQuickLinks.value,
    legalLinks: getLegalLinks({ useUrl }),
    cncLogo: { show: true },
    translations: { tFollowUs: t('t_FOLLOWUSON') },
    brandLogo: isMeetTheWorldEnabled,
  };
});

// ### ON MOUNTED
onMounted(() => {
  const legalLinks = wdsFooterRef?.value?.$el?.querySelector('.footer-area.legal-links');
  if (legalLinks) {
    legalLinks.insertAdjacentHTML('beforeend', '<div id="teconsent">');
    legalLinks.setAttribute('id', 'footerLegalLinks');
  }
});
</script>

<template>
  <WdsFooter
    ref="wdsFooterRef"
    class="footer-web"
    v-bind="getFooterData"
    @language-picker-click="languageStore.toggleLanguagePicker(true)"
    @currency-picker-click="currencyStore.toggleCurrencyPicker(true)"
  />
</template>

<style lang="scss" scoped>
.footer-web {
  padding-bottom: wds-rem(56px);

  @include desktop {
    padding-bottom: 0;
  }
}
</style>

<style lang="scss">
/* stylelint-disable-next-line selector-class-pattern */
.truste_cursor_pointer {
  color: $wds-color-white;
  display: inline-flex;
  align-items: center;
  min-height: $wds-spacing-xxl;
  min-width: $wds-spacing-xl;
  padding: 0 $wds-spacing-xs;

  &:hover {
    text-decoration: none;
    color: $wds-color-white;
  }
}

#teconsent a {
  color: $wds-color-white;
}
</style>
